import { useEffect, useRef } from "react";
import { useSession } from "@/lib/session";
import {
  syncSavedItems,
  type SavedItem,
  type SavedKind,
} from "@/lib/saved-items.functions";
import { saveItems } from "@/hooks/use-bookmarks";

const STORAGE_KEY = "flaneriet-bookmarks";

function readLocal(): SavedItem[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    const out: SavedItem[] = [];
    for (const k of parsed) {
      const idx = k.indexOf(":");
      if (idx > 0) {
        const kind = k.slice(0, idx) as SavedKind;
        const id = k.slice(idx + 1);
        if (kind === "article" || kind === "recipe" || kind === "video") {
          out.push({ kind, id });
        }
      } else if (/^[0-9a-f-]{36}$/i.test(k)) {
        // gammel form
        out.push({ kind: "article", id: k });
      }
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Monteres én gang i __root. Ved sign-in:
 * - læs lokale bogmærker (med både ny og gammel form)
 * - kald syncSavedItems (auto-merge/union)
 * - skriv den flettede liste tilbage til localStorage
 */
export function useCloudBookmarkSync() {
  const session = useSession();
  const syncedForUser = useRef<string | null>(null);

  useEffect(() => {
    if (!session) {
      syncedForUser.current = null;
      return;
    }
    if (syncedForUser.current === session.user.id) return;
    syncedForUser.current = session.user.id;

    const local = readLocal();
    const isUuid = (s: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    const clean = local.filter((it) => isUuid(it.id));

    syncSavedItems({ data: { localItems: clean } })
      .then((merged) => {
        if (Array.isArray(merged)) saveItems(merged);
      })
      .catch(() => {
        // stille – lokal state overlever
      });
  }, [session]);
}
